import { useEffect, useState } from "react";
import { Card, CardContent, Stack, Typography, Chip, Divider, Link, Button } from "@mui/material";
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import dayjs from "dayjs";
import { toast } from "sonner";
import { MyNextAppointment } from "../../services/my-appointments.service";
import { PixPendingPayment } from "../../services/payment.service";
import { PixQrModal } from "../Checkout/PixQrModal";

interface UpcomingAppointmentCardProps {
  appt: MyNextAppointment;
  pendingPix?: PixPendingPayment | null;
}

function formatRemaining(ms: number) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export function UpcomingAppointmentCard({ appt, pendingPix }: UpcomingAppointmentCardProps) {
  const [openPixQr, setOpenPixQr] = useState(false);
  const [, setTick] = useState(0);

  const expiresMs = pendingPix?.expiresAt ? new Date(pendingPix.expiresAt).getTime() : NaN;
  const remainingMs = expiresMs - Date.now();
  const showPix = appt.status === 'PAGAMENTO_PENDENTE' && !!pendingPix?.pixCopiaCola;
  const expired = !showPix || Number.isNaN(expiresMs) || remainingMs <= 0;

  useEffect(() => {
    if (!showPix) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [showPix]);

  const handleCopyPix = async () => {
    if (!pendingPix?.pixCopiaCola) return;
    try {
      await navigator.clipboard.writeText(pendingPix.pixCopiaCola);
      toast.success('Código Pix copiado!');
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  return (
    <>
      <Card sx={{ width: "100%", maxWidth: 800, mx: "auto" , boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <CardContent>
          <Stack spacing={1}>
          <Typography variant="h6" fontWeight={600} color="#133859">
            Sua próxima consulta
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">Médico:</Typography>
            <Chip label={appt.doctorName || "-"} size="small" />
          </Stack>

          {appt.local && (
            <Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">Local:</Typography>
                <Chip label={appt.local.localName || appt.local.id} size="small" />
              </Stack>
                    
              {(appt.local.address || appt.local.city || appt.local.state) && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {`${appt.local.address || ""}${appt.local.city || appt.local.state ? " " : ""}${appt.local.city || ""}${appt.local.state ? ` / ${appt.local.state}` : ""}`}
                  </Typography>
                  <Link
                    href={`https://www.google.com/maps?q=${encodeURIComponent(`${appt.local.address || ""} ${appt.local.city || ""} ${appt.local.state || ""}`.trim())}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                  >
                    <Typography variant="caption" color="primary">
                      Abrir no Maps
                    </Typography>
                    <OpenInNewOutlinedIcon sx={{ width: 16, ml: 0.3 }}  color="primary"/>
                  </Link>
                </Stack>
              )}
            </Stack>
          )}

          {appt.status === 'CONFIRMADO' && appt.pin && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">PIN da consulta:</Typography>
              <Typography fontWeight={600} variant="body2" color="primary">{appt.pin}</Typography>
            </Stack>
          )}

          {showPix && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Pagamento pendente — tempo para pagar:
                </Typography>
                <Chip
                  size="small"
                  color={expired ? 'default' : 'warning'}
                  label={expired ? 'Expirado' : formatRemaining(remainingMs)}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="outlined" onClick={handleCopyPix} disabled={expired}>
                  Copiar Pix
                </Button>
                <Button variant="outlined" onClick={() => setOpenPixQr(true)} disabled={expired}>
                  Ver QRCode
                </Button>
              </Stack>
            </Stack>
          )}

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" spacing={4}>
            <Stack>
              <Typography variant="caption" color="text.secondary">Data</Typography>
              <Typography fontWeight={600}color="#133859">{dayjs(appt.startTime).format("DD/MM/YYYY")}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">Início</Typography>
              <Typography fontWeight={600} color="#133859">{dayjs(appt.startTime).format("HH:mm")}</Typography>
            </Stack>
            <Stack>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Chip
                size="small"
                color={appt.status === 'CONFIRMADO' ? 'success' : 'warning'}
                label={appt.status}
              />
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
      </Card>

      <PixQrModal
        open={openPixQr}
        onClose={() => setOpenPixQr(false)}
        payload={pendingPix?.pixCopiaCola ?? ''}
        amountLabel={'Pagamento'}
        expiresAt={pendingPix?.expiresAt ?? null}
      />
    </>
  );
}
